// TableView.js — ONE unified hierarchical heatmap: acts → sequences → scenes
// nested in a single table (they share the 12 D·E·R axes). Rollup (D/E/R) leads,
// then the 12 axes grouped Design / Execution / Read. Score cells are clickable
// CHIPS; hovering a cell shows a styled card with that axis's executive summary
// for that unit (cell_notes from the prose pass) — not the bare score, not the
// HTML title. Clicking a cell opens the panel focused on THAT axis (what's going
// on), not the Fix-it section. Header (i) shows the axis definition.

import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { grainPlural } from './grainNouns';
import { AXIS_INFO } from './axisInfo';
import { verdictFromLenses, BAND_STYLE, bandLabel } from './verdictModel';
import { unitDisplayLabel, unitSortKey, unitTitle } from './unitLabels';
// ⚠️ ALIASED: this file already has a `tip` state variable for its own rich hover card, and the
// bare import shadowed it — `TypeError: tip is not a function`, which the error boundary turned
// into a blank "Widget error" panel for the whole widget.
import { tip as hoverTip } from './Tip';
import { fmtScore } from './Constants';

const NAVY = '#14536e';

const GROUPS = [
  { lens: 'Rollup', cols: [
    { key: 'design', ab: 'D', name: 'Design', def: 'Is it built right — contract, pressure, development, movement, consequence.' },
    { key: 'execution', ab: 'E', name: 'Execution', def: 'Does the rendering deliver it — economy, assembly, clarity.' },
    { key: 'read', ab: 'R', name: 'Involvement', def: 'Does it draw you in — freshness, unpredictability, charge, grip.' },
  ] },
  { lens: 'Design', cols: [
    { key: 'contract', ab: 'Co', name: 'Contract', def: 'Organized around a determinate governing thread.' },
    { key: 'pressure', ab: 'Pr', name: 'Pressure', def: 'A page-visible force that drives progression.' },
    { key: 'development', ab: 'Dv', name: 'Development', def: 'The thread changes vs merely repeats.' },
    { key: 'movement', ab: 'Mv', name: 'Movement', def: 'Entry-state → exit-state change of the thread.' },
    { key: 'consequence', ab: 'Cq', name: 'Consequence', def: 'The result obliges later units.' },
  ] },
  { lens: 'Execution', cols: [
    { key: 'economy', ab: 'Ec', name: 'Economy', def: 'No avoidable waste.' },
    { key: 'assembly', ab: 'As', name: 'Assembly', def: 'Sub-units well ordered / paced / seamed.' },
    { key: 'clarity', ab: 'Cl', name: 'Clarity', def: 'The audience can follow what matters.' },
  ] },
  { lens: 'Involvement', cols: [
    { key: 'freshness', ab: 'Fr', name: 'Freshness', def: 'Non-rote vs the expected version.' },
    { key: 'unpredictability', ab: 'Un', name: 'Unpredictability', def: 'Fairly defeats anticipation.' },
    { key: 'charge', ab: 'Ch', name: 'Charge', def: 'Felt emotion evoked, not displayed.' },
    { key: 'grip', ab: 'Gr', name: 'Grip', def: 'At the exit, pulled into the next unit.' },
  ] },
];
const ALL_COLS = GROUPS.flatMap(g => g.cols.map(c => ({ ...c, lens: g.lens })));
const GROUP_START = new Set(['design', 'contract', 'economy', 'freshness']);
const HOVER_Q = '(hover: hover) and (pointer: fine)';
const canHoverNow = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(HOVER_Q).matches;

function useCanHover() {
  const [canHover, setCanHover] = useState(canHoverNow);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(HOVER_Q);
    const on = () => setCanHover(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, []);
  return canHover;
}

const fmt = fmtScore;   // was a local copy; one definition now lives in Constants.js
function pctOf(val, peers) {
  const xs = peers.filter(x => typeof x === 'number' && !isNaN(x));
  if (val == null || xs.length < 4) return null;
  return Math.round((xs.filter(x => x <= val).length / xs.length) * 100);
}
function ordinal(n) {
  if (n == null) return null;
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
// "Not owed" = this axis carries no obligation for this unit, so the absence of a
// score is DELIBERATE, not missing. We render it as a clearly neutral chip (muted
// "—" on a faint gray pill) so it never reads as a broken/uncoloured number.
function isNotOwed(obligation, status) {
  return status === 'na' || status === 'not_owed' || obligation === 'light' || obligation === 'not_owed';
}
// higher-contrast text colour for a fill (white on deep reds, ink on light/greens)
function textOnFill(bg) {
  if (!bg || bg[0] !== '#' || bg.length < 7) return '#0f172a';
  const lin = (h) => { const c = parseInt(h, 16) / 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
  const L = 0.2126 * lin(bg.slice(1, 3)) + 0.7152 * lin(bg.slice(3, 5)) + 0.0722 * lin(bg.slice(5, 7));
  return (1.05 / (L + 0.05)) >= ((L + 0.05) / 0.05) ? '#fff' : '#0f172a';
}
function chip(score, obligation, status, scale) {
  // Genuinely no data (no score and no obligation signal) — keep as a quiet dot.
  if (score == null && !isNotOwed(obligation, status)) {
    return { bg: 'transparent', fg: '#cbd5e1', bd: 'transparent', txt: '·', faint: true, notOwed: false };
  }
  // Not owed for this unit — a deliberate neutral "n/a" treatment, distinct from a low score.
  if (isNotOwed(obligation, status)) {
    return { bg: '#f1f5f9', fg: '#94a3b8', bd: '#e2e8f0', txt: '—', faint: true, notOwed: true };
  }
  const s = parseFloat(score);
  // When a per-score scale is supplied, give every score its own fill so the lowest pops (not 3 bands).
  if (scale && Number.isFinite(s)) {
    const bg = scale[Math.max(0, Math.min(scale.length - 1, Math.round(s) - 1))];
    return { bg, fg: textOnFill(bg), bd: bg, txt: fmt(s), faint: false, notOwed: false };
  }
  let bg, fg, bd;
  if (s < 5) { bg = '#fde7e6'; fg = '#a3261e'; bd = '#f3c9c6'; }
  else if (s <= 6) { bg = '#fbefdc'; fg = '#8a5a12'; bd = '#f0dcb0'; }   // amber-ish "functional" like the reference
  else { bg = '#e3f3e6'; fg = '#1f7a37'; bd = '#bfe3c7'; }
  return { bg, fg, bd, txt: fmt(s), faint: false, notOwed: false };
}

function rowLabel(kind, dc) {
  if (kind === 'act') return unitDisplayLabel(dc, 'table');
  if (kind === 'sequence') {
    const label = unitDisplayLabel(dc, 'table');
    const title = unitTitle(dc);
    return title ? `${label}: ${title}` : label;
  }
  const label = unitDisplayLabel(dc, 'table') || `Scene ${dc.number}`;
  const title = dc.title && dc.title !== label ? `${label}: ${dc.title}` : label;
  return dc.page != null ? `p.${dc.page} · ${title}` : title;
}

export default function TableView({ nodes, onSelectAxis, onSelectAxisHeader, selectedId, scoreScale = null, script = null, isChatted = null }) {
  const canHover = useCanHover();
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [tip, setTip] = useState(null);   // cell hover: {x,y,kicker,head,body,foot}
  const [hdr, setHdr] = useState(null);   // header hover: {x,y,col} — interactive (stays put)
  const [wiki, setWiki] = useState(null); // open wiki drawer for a column key
  const hideTimer = useRef(null);
  useEffect(() => {
    if (!canHover) {
      setTip(null);
      setHdr(null);
    }
  }, [canHover]);
  const toggle = useCallback((id) => {
    setCollapsed(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  }, []);

  // Interactive header popover: opens on header hover, persists while the mouse is over the
  // header OR the popover (so you can move onto it and click "Show more"), closes after a beat.
  const openHdr = (e, col) => {
    if (!canHover) return;
    if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
    const r = e.currentTarget.getBoundingClientRect();
    setHdr({ x: r.left + r.width / 2, y: r.bottom, col });
  };
  const scheduleHideHdr = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setHdr(null), 160);
  };
  const cancelHideHdr = () => { if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; } };

	  const rows = useMemo(() => {
	    const byType = (t) => nodes.filter(n => n.nodeType === t && n.detailedContent);
	    const sortN = (arr) => arr.slice().sort((a, b) => unitSortKey(a) - unitSortKey(b));
    const acts = sortN(byType('act'));
    const seqs = byType('sequence');
    const scenes = byType('scene');
    const out = [];

    // Grain-adaptive nesting. Feature: act → sequence → scene. TV: sequence → scene
    // (no acts). Short: scene only (no acts, no sequences). The table must render for
    // all three, so we don't assume acts exist as the top level.
    const pushScenesOf = (parentId, level) =>
      sortN(scenes.filter(x => x.parent === parentId)).forEach(s =>
        out.push({ node: s, kind: 'scene', level, hasChildren: false }));
    const pushSeq = (seq, level) => {
      const sc = scenes.filter(x => x.parent === seq.id);
      out.push({ node: seq, kind: 'sequence', level, hasChildren: sc.length > 0 });
      if (!collapsed.has(seq.id)) pushScenesOf(seq.id, level + 1);
    };

    if (acts.length) {
      const actIds = new Set(acts.map(a => a.id));
      acts.forEach(act => {
        const cs = sortN(seqs.filter(s => s.parent === act.id));
        out.push({ node: act, kind: 'act', level: 0, hasChildren: cs.length > 0 });
        if (collapsed.has(act.id)) return;
        cs.forEach(seq => pushSeq(seq, 1));
      });
      // defensive: any sequences not parented under an act still get a home
      sortN(seqs.filter(s => !actIds.has(s.parent))).forEach(seq => pushSeq(seq, 0));
    } else if (seqs.length) {
      sortN(seqs).forEach(seq => pushSeq(seq, 0));          // TV: episode → sequences → scenes
    } else {
      sortN(scenes).forEach(s => out.push({ node: s, kind: 'scene', level: 0, hasChildren: false })); // short
    }
    return out;
  }, [nodes, collapsed]);

  // Within-grain score distributions, per axis — the baseline for the position cue
  // (a raw 7 means different things at scene vs act, per the calibration finding).
  const peerStats = useMemo(() => {
    const m = {};
    nodes.forEach(n => {
      const t = n.nodeType;
      if (t !== 'act' && t !== 'sequence' && t !== 'scene') return;
      const sc = n.detailedContent?.scores || {};
      m[t] = m[t] || {};
      ALL_COLS.forEach(c => {
        const v = sc[c.key]?.score;
        if (v != null) (m[t][c.key] = m[t][c.key] || []).push(parseFloat(v));
      });
    });
    return m;
  }, [nodes]);

  // show the styled hover card for a cell (axis exec-summary) or header (definition).
  const showTip = (e, { kicker, head, body, foot }) => {
    if (!canHover) return;
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ x: r.left + r.width / 2, y: r.top, kicker, head, body, foot });
  };
  const hideTip = () => setTip(null);

  // ⚠️ The site stylesheet forces `table > thead > tr > th { background: #083a54 !important; color: #fff }`,
  // which an inline background cannot beat, so the header IS navy: text in it must be light. (The
  // column abbreviations were NAVY on navy, i.e. invisible — found 2026-10-15.)
  const th = { position: 'sticky', background: '#fff', zIndex: 2, fontWeight: 600, color: NAVY, padding: '5px 0', fontSize: '.72rem', textAlign: 'center', borderBottom: `1.5px solid ${NAVY}22` };
  const ROW_BG = { act: '#eaf1f6', sequence: '#f6f9fb', scene: '#fff' };
  const ROW_FW = { act: 700, sequence: 600, scene: 400 };

  return (
    <div className="d-flex flex-column" style={{ height: '100%' }}>
      <div className="d-flex align-items-center px-3 pt-3 pb-2 flex-shrink-0" style={{ gap: 12 }}>
        <span style={{ fontWeight: 600, color: NAVY }}>All units · D·E·R grid</span>
        <span style={{ color: '#94a3b8', fontSize: '.74rem' }}>
          {/* Corrected 2026-08-13: the pill is band-driven (verdictFromLenses → BAND_STYLE, below),
              so explaining it with numeric cutpoints was wrong on both counts — the real cuts are
              per-LENS (>6 solid · >4–6 soft · ≤4 broken, verdictModel.js:23-24) and then GATED, and
              BAND_STYLE has four colours, not three. */}
          verdict pill = triage band · Keep · Polish · Rework · Rebuild · gray “—” = not owed ·{' '}
          {canHover ? 'hover a cell for the note · click a cell to open that axis' : 'tap a score to open that axis · tap ⓘ for definitions'}
        </span>
      </div>

      <div className="flex-grow-1 overflow-auto px-3 pb-3" style={{ minHeight: 0 }}>
        <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: '.82rem', width: '100%', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 'auto' }} />
            <col style={{ width: 96 }} />
            {ALL_COLS.map(c => <col key={c.key} style={{ width: 34 }} />)}
          </colgroup>
          <thead>
            <tr>
              <th style={{ ...th, top: 0, left: 0, zIndex: 4, textAlign: 'left', paddingLeft: 8 }}>Unit</th>
              <th style={{ ...th, top: 0, borderLeft: '1px solid #e8eef3' }}>Verdict</th>
              {GROUPS.map(g => (
                <th key={g.lens} colSpan={g.cols.length} style={{ ...th, top: 0, borderLeft: '1px solid #e8eef3' }}>{g.lens}</th>
              ))}
            </tr>
            <tr>
              <th style={{ ...th, top: 26, left: 0, zIndex: 4, textAlign: 'left', paddingLeft: 8 }}></th>
              <th style={{ ...th, top: 26, borderLeft: '1px solid #e8eef3', fontWeight: 500, color: '#a9cbdc', fontSize: '.62rem', textTransform: 'uppercase', letterSpacing: '.04em' }}>triage</th>
              {ALL_COLS.map(c => (
                <th key={c.key}
                  onMouseEnter={canHover ? (e) => openHdr(e, c) : undefined}
                  onMouseLeave={canHover ? scheduleHideHdr : undefined}
                  style={{ ...th, top: 26, borderLeft: GROUP_START.has(c.key) ? '1px solid #e8eef3' : 'none', cursor: canHover ? 'help' : 'default' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    {onSelectAxisHeader
                      ? <button type="button" onClick={(e) => { e.stopPropagation(); setHdr(null); onSelectAxisHeader(c.key); }}
                          aria-label={`Open ${c.name} inspector`}
                          style={{ all: 'unset', cursor: 'pointer', color: '#fff', fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 2, textDecorationColor: 'rgba(255,255,255,.45)' }}>{c.ab}</button>
                      : <span>{c.ab}</span>}
                    <button type="button"
                      onClick={(e) => { e.stopPropagation(); setHdr(null); setWiki(c.key); }}
                      aria-label={`Open ${c.name} definition`}
                      style={{
                        all: 'unset', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 16, height: 16, borderRadius: 999, color: '#a9cbdc', cursor: 'pointer',
                        fontSize: '.65rem', lineHeight: 1, marginLeft: 1,
                      }}>
                      ⓘ
                    </button>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ node, kind, level, hasChildren }) => {
              const dc = node.detailedContent;
              const sc = dc.scores || {};
              const notes = dc.prose?.cell_notes || {};
              const axesCards = dc.prose?.axes || {};
              const vrd = dc.prose?.verdict || {};
              const sel = node.id === selectedId;
              const isCollapsed = collapsed.has(node.id);
              const bg = sel ? '#dce9f1' : ROW_BG[kind];
              const label = rowLabel(kind, dc);
              // Triage verdict for this unit — the weight-free band from its three
              // lens rollups (Design / Execution / Read). Grain = this unit's type.
              const grain = ['scene', 'sequence', 'act'].includes(node.nodeType) ? node.nodeType : 'scene';
              const lensScore = {
                design: sc.design?.score != null ? parseFloat(sc.design.score) : null,
                execution: sc.execution?.score != null ? parseFloat(sc.execution.score) : null,
                read: sc.read?.score != null ? parseFloat(sc.read.score) : null,
              };
              const verdict = verdictFromLenses(lensScore, grain);
              const bandStyle = verdict.band ? BAND_STYLE[verdict.band] : null;
              const bandText = verdict.band ? bandLabel(verdict.band, grain) : null;
              return (
                <tr key={node.id}>
                  <td onClick={() => onSelectAxis(node, null)} style={{
                    position: 'sticky', left: 0, zIndex: 1, background: bg, cursor: 'pointer',
                    padding: '4px 8px', paddingLeft: 8 + level * 18, whiteSpace: 'nowrap',
                    overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left',
                    fontWeight: ROW_FW[kind], color: kind === 'scene' ? '#334155' : '#0f172a',
                    borderBottom: '1px solid #eef2f6', borderLeft: sel ? `3px solid ${NAVY}` : '3px solid transparent',
                    fontSize: kind === 'act' ? '.9rem' : '.82rem',
                  }}>
                    {hasChildren ? (
                      <span onClick={(e) => { e.stopPropagation(); toggle(node.id); }}
                        style={{ display: 'inline-block', width: 14, color: NAVY, cursor: 'pointer' }}>
                        {isCollapsed ? '▸' : '▾'}
                      </span>
                    ) : <span style={{ display: 'inline-block', width: 14 }} />}
                    {label}
                    {/* the same teal "you asked about this" mark as the timeline and List */}
                    {isChatted && isChatted(node) && (
                      <span title="You have a chat here" aria-label="has a chat"
                        style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 999, background: '#14b8a6', marginLeft: 7, verticalAlign: 'middle', boxShadow: '0 0 0 1.5px #fff' }} />
                    )}
                  </td>
                  <td onClick={() => onSelectAxis(node, null)}
                    {...hoverTip(canHover ? (bandText ? `Triage: ${bandText} — overall verdict for this ${grain}` : 'No verdict yet for this unit') : null)}
                    style={{
                      textAlign: 'center', padding: '3px 4px 4px', background: sel ? '#dce9f1' : 'transparent',
                      borderBottom: '1px solid #f5f8fa', borderLeft: '1px solid #eef2f6', cursor: 'pointer',
                    }}>
                    {bandStyle ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: '100%',
                        padding: '1px 8px 2px', borderRadius: 999,
                        background: bandStyle.bg, color: bandStyle.fg, border: `1px solid ${bandStyle.bd}`,
                        fontWeight: 700, fontSize: '.66rem', letterSpacing: '.02em', whiteSpace: 'nowrap',
                        overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: bandStyle.fg, flexShrink: 0 }} />
                        {bandText}
                      </span>
                    ) : (
                      <span style={{ color: '#cbd5e1', fontSize: '.8rem' }}>·</span>
                    )}
                  </td>
                  {ALL_COLS.map(c => {
                    const isRollup = c.lens === 'Rollup';
                    const cell = sc[c.key] || {};
                    const ch = chip(cell.score, cell.obligation, cell.status, scoreScale);
                    const summary = ch.notOwed
                      ? `This axis isn’t owed for this ${node.nodeType || 'unit'} — it carries no obligation here, so there’s no score to read. This is deliberate, not a gap.`
                      : isRollup
                      ? (vrd[`${c.key}_summary`] || `Rollup of its ${c.name.toLowerCase()} axes.`)
                      : (axesCards[c.key]?.cell_note || notes[c.key] || c.def);
                    // within-grain position (a raw 7@act ≠ 7@scene — show the grain-relative percentile)
                    const pv = (ch.notOwed || cell.score == null)
                      ? null : pctOf(parseFloat(cell.score), (peerStats[node.nodeType] && peerStats[node.nodeType][c.key]) || []);
                    return (
                      <td key={c.key}
                        onClick={() => onSelectAxis(node, isRollup ? null : c.key)}
                        {...hoverTip(canHover && ch.notOwed ? 'not owed for this unit' : null)}
                        onMouseEnter={canHover ? (e) => showTip(e, {
                          kicker: `${c.name}${cell.obligation ? ' · ' + cell.obligation : ''}`,
                          head: ch.notOwed ? 'not owed' : (ch.txt === '·' ? 'n/a' : ch.txt),
                          body: summary,
                          foot: (pv != null ? `${ordinal(pv)} percentile among ${grainPlural(script, node.nodeType, 'units')} · ` : '') + `click to open ${label} →`,
                        }) : undefined}
                        onMouseLeave={canHover ? hideTip : undefined}
                        style={{
                          textAlign: 'center', padding: '3px 2px 4px', background: sel ? '#dce9f1' : 'transparent',
                          borderBottom: '1px solid #f5f8fa', borderLeft: GROUP_START.has(c.key) ? '1px solid #eef2f6' : 'none', cursor: 'pointer',
                        }}>
                        <span style={{
                          display: 'inline-block', minWidth: 24, padding: '1px 4px', borderRadius: 6,
                          background: ch.bg, color: ch.fg, border: ch.bd === 'transparent' ? 'none' : `1px solid ${ch.bd}`,
                          fontWeight: isRollup ? 700 : 600, fontSize: isRollup ? '.82rem' : '.8rem',
                        }}>{ch.txt}</span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* custom hover card (replaces the native title) */}
      {canHover && tip && (
        <div style={{
          position: 'fixed', left: tip.x, top: tip.y, transform: 'translate(-50%, calc(-100% - 10px))',
          maxWidth: 290, zIndex: 50, pointerEvents: 'none',
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 8px 28px rgba(15,23,42,.16)',
          padding: '12px 14px',
        }}>
          <div className="d-flex align-items-baseline" style={{ gap: 8 }}>
            <span style={{ fontSize: '.66rem', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: NAVY }}>{tip.kicker}</span>
            {tip.head && <span style={{ marginLeft: 'auto', fontWeight: 800, color: '#0f172a' }}>{tip.head}</span>}
          </div>
          {tip.body && <div style={{ marginTop: 6, color: '#334155', fontSize: '.86rem', lineHeight: 1.45 }}>{tip.body}</div>}
          {tip.foot && <div style={{ marginTop: 8, color: NAVY, fontSize: '.75rem', fontWeight: 600 }}>{tip.foot}</div>}
        </div>
      )}

      {/* Interactive header popover — title (left) / kind (right), stays put so you can
          move onto it and click Show more for the wiki. */}
      {canHover && hdr && (
        <div
          onMouseEnter={cancelHideHdr}
          onMouseLeave={scheduleHideHdr}
          style={{
            position: 'fixed', left: hdr.x, top: hdr.y + 6, transform: 'translateX(-50%)',
            maxWidth: 280, zIndex: 55, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
            boxShadow: '0 8px 28px rgba(15,23,42,.16)', padding: '11px 13px',
          }}
        >
          <div className="d-flex align-items-baseline" style={{ gap: 8 }}>
            <span style={{ fontWeight: 800, color: '#0f172a', fontSize: '.92rem' }}>{hdr.col.name}</span>
            <span style={{ marginLeft: 'auto', fontSize: '.64rem', fontWeight: 700, letterSpacing: '.05em',
              textTransform: 'uppercase', color: NAVY }}>
              {hdr.col.lens === 'Rollup' ? 'Group' : `Axis · ${hdr.col.lens}`}
            </span>
          </div>
          <div style={{ marginTop: 6, color: '#334155', fontSize: '.84rem', lineHeight: 1.45 }}>{hdr.col.def}</div>
          <button type="button" onClick={() => { setWiki(hdr.col.key); setHdr(null); }}
            className="btn btn-link btn-sm p-0 text-decoration-none mt-2"
            style={{ color: NAVY, fontWeight: 700, fontSize: '.78rem' }}>
            Show more →
          </button>
        </div>
      )}

      {/* Wiki drawer — fuller, shared definition (axisInfo) for the selected column. */}
      {wiki && AXIS_INFO[wiki] && (() => {
        const info = AXIS_INFO[wiki];
        return (
          <>
            <div onClick={() => setWiki(null)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.18)', zIndex: 70 }} />
            <div style={{
              position: 'fixed', top: 0, right: 0, bottom: 0, left: canHover ? 'auto' : 0,
              width: canHover ? 360 : '100%', maxWidth: canHover ? '92%' : '100%', zIndex: 71,
              background: '#fff', borderLeft: canHover ? '1px solid #e2e8f0' : 'none',
              boxShadow: canHover ? '-10px 0 30px rgba(15,23,42,.16)' : 'none',
              padding: '18px 20px', overflowY: 'auto',
            }}>
              <div className="d-flex align-items-center" style={{ marginBottom: 14 }}>
                <span style={{ fontSize: '.66rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: NAVY }}>
                  {info.lens && info.lens !== 'overview' ? `${info.lens}` : 'Reference'}
                </span>
                <button type="button" onClick={() => setWiki(null)}
                  className="btn btn-link btn-sm p-0 text-decoration-none ms-auto"
                  style={{ color: NAVY, fontWeight: 700 }}>✕ Close</button>
              </div>
              <h3 style={{ color: '#0f172a', fontWeight: 800, fontSize: '1.3rem', margin: '0 0 10px' }}>{info.label}</h3>
              <p style={{ color: '#334155', fontSize: '.95rem', lineHeight: 1.55, margin: '0 0 14px' }}>{info.measures}</p>
              {info.scale && (
                <div style={{ background: '#f6f9fb', border: '1px solid #e8eef3', borderRadius: 8, padding: '9px 12px', fontSize: '.82rem', color: '#48606c' }}>
                  <strong style={{ color: NAVY }}>How it scores: </strong>{info.scale}
                </div>
              )}
            </div>
          </>
        );
      })()}
    </div>
  );
}
TableView.propTypes = {
  nodes: PropTypes.array.isRequired,
  onSelectAxis: PropTypes.func.isRequired,
  selectedId: PropTypes.string,
  scoreScale: PropTypes.array,
  isChatted: PropTypes.func,
};
